import React, { useEffect, useMemo, useState } from "react";
import { CpnwContainer } from "hooks/useCpnw";
import DashboardPassportTable from "components/DashboardPassportTable";
import DashboardPassportItemFields from "components/DashboardPassportItem/DashboardPassportItemFields";

import { Loading } from "@cpnw/ui";

import styles from "./HcPassport.module.css";
import { CanPermissions } from "pages/enums";
import { AccountContainer } from "hooks/useAccount";
import { RequirementContainer } from "hooks/useRequirement";

const HcPassport = ({ userRoles }) => {
  const {
    learningModulesData,
    refetchLearningModules,
    reportData,
    reportRefetch,
    assignmentAccept,
    assignmentReject,
    getPassports,
    downloadPassports,
    uploadPassports,
    deletePassports
  } = CpnwContainer.useContainer();

  const { hasCanPermissions } = AccountContainer.useContainer();
  const { requirementFacilitiesData } = RequirementContainer.useContainer();
  console.log("requirement by facilities", requirementFacilitiesData)

  const showUpload = hasCanPermissions(CanPermissions.CanUpload)
  const showDownload = hasCanPermissions(CanPermissions.CanDownload)
  const showDelete = hasCanPermissions(CanPermissions.CanDelete)
  const isHC = userRoles && userRoles.includes("HEALTHCARE");

  // endpoints are disabled by default, refetch both first
  useEffect(() => {
    reportRefetch();
  }, [])

  const [reports, setReports] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (reportData) {
      console.log("[HcPassport] reportData received:", reportData);
      const contacts = Array.isArray(reportData.data) ? reportData.data : reportData.data?.data || [];
      console.log("[HcPassport] contacts extracted:", contacts.length);
      setReports(contacts);
      setDataLoading(false);
    }
  }, [reportData]);

  // Keep the table input stable between renders. Passing getData(...) directly
  // creates a new array on every render and can cause the sortable-table hook to
  // replace the currently filtered rows with the full data set.
  const tableData = useMemo(() => getData(reports, isHC), [reports, isHC]);

  const assignmentReview = async (assignmentId, reviewStatus, rejectionContent) => {
    try {
      if (reviewStatus == "Accepted") {
        await assignmentAccept(assignmentId);
        return true;
      }

      if (reviewStatus == "Rejected") {
        await assignmentReject(assignmentId, rejectionContent);
        return true;
      }
    } catch (error) {
      console.log(error);
    }

    return false;
  };

  const passportUpload = async (studentContactId, files) => {
    try {
      const rsp = await uploadPassports(studentContactId, files);
      return rsp?.data || [];
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const passportDelete = async (studentContactId, fileName) => {
    try {
      const rsp = await deletePassports(studentContactId, fileName);
      return rsp?.data == true;
    } catch (error) {
      console.log(error);
    }

    return false;
  };

  const onDownloadContactPassports = async (contactId, passports) => {
    try {
      // let fileNames = passports?.map((m) => m.name);
      let fileNames = passports
      if (fileNames?.length > 0) await downloadPassports(contactId, fileNames);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {dataLoading && (
          <div>
            <Loading />
          </div>
        )}
        {reports?.length > 0 ? (
          <DashboardPassportTable
            data={tableData}
            columns={columns(isHC)}
            passportsData={null}
            assignmentReview={assignmentReview}
            passportUpload={passportUpload}
            passportDelete={passportDelete}
            onGetPassportsData={getPassports}
            onDownloadContactPassports={(contactId, passports) =>
              onDownloadContactPassports(contactId, passports)
            }
            showDelete={showDelete}
            showDownload={showDownload}
            showUpload={showUpload}
            userRoles={userRoles}
          />
        ) : null}
      </div>
    </div>
  );
};

const getData = (data, isHC) => {
  const result = (data || []).map((m) => {
    let row = {
      ...m,
      rejectionContent: m.Assignment?.RejectionReasons,
      Assignment: m.Assignment || null,
      Assignments: m.Assignments || [],
      contact: {
        id: m.ContactIDPK,
        name: m.NameLast + ", " + m.NameFirst,
        email: m.email,
        phone: m.PrimaryPhone,
        emergencyContact: m.EmergencyContact,
        emergencyContactPhone: m.EmergencyPhone,
        school: m.EDFacilityName,
        programId: m.BusinessUnitId,
        program: m.EDProgramName || m.BusinessUnitName,
        dob: m.DOB,
        sIDEID: m.IndividualID,
        location: m.HCFacilityName || m.Assignment?.HCFacilityName,
        startDate: m.StartDate || m.Assignment?.StartDate,
        address1: m.Address1,
        address2: m.Address2,
        city: m.City,
        state: m.State,
        zip: m.Zip,
        ssn: m.SSN,
      },
      docs: m.ContactIDPK,
      oig: m.OIGResult,
      sam: m.SAMResult,
      userType: m.TypeContact,
    };

    if (isHC) {
      row = {
        ...row,
        id: m.Assignment?.AssignmentIDpk,
        assignmentId: m.Assignment?.AssignmentIDpk,
        assignmentStatus: getAssignmentStatusTypes(m.Assignment?.Status),
        isHC: true,
      };
    } else {
      row = {
        ...row,
        id: m.ContactIDPK,
      };
    }

    return row;
  });
  return result;
};

const getAssignmentStatusTypes = (status) => {
  switch (status) {
    case 0:
      return "Accepted";
    case 1:
      return "Rejected";
    case 2:
      return "Review";
    default:
      return "";
  }
};



const columns = (isHC) => [
  {
    label: "Status",
    accessor: DashboardPassportItemFields.assignmentStatus,
    sortable: true,
    hide: !isHC
  },
  {
    label: "Name",
    accessor: DashboardPassportItemFields.nameOfContact,
    sortable: true,
  },
  {
    label: "DOCS",
    accessor: DashboardPassportItemFields.additionalDocuments,
    sortable: false,
    sortbyOrder: "desc",
  },
  {
    label: "CPNW",
    accessor: DashboardPassportItemFields.CPNWSpecificRequirements,
    sortable: true,
  },
  {
    label: "ED",
    accessor: DashboardPassportItemFields.educationSiteSpecificRequirements,
    sortable: true,
  },
  {
    label: "HC",
    accessor: DashboardPassportItemFields.healthcareSiteSpecificRequirement,
    sortable: true,
  },
  { label: "OIG", accessor: DashboardPassportItemFields.oig, sortable: true },
  { label: "SAM", accessor: DashboardPassportItemFields.sam, sortable: true },
  { label: "BGC", accessor: DashboardPassportItemFields.backgroundCheck, sortable: true },
  {
    label: "WATCH",
    accessor: DashboardPassportItemFields.watchReports,
    sortable: true,
  },
  {
    label: "Type",
    accessor: DashboardPassportItemFields.userContactType,
    sortable: true,
  },
].filter(m => !m.hide);

export default HcPassport;